import { Stamp } from '@/components/UI/Stamp'
import type { StampShape } from '@/components/UI/Stamp'
import { IconCheck } from '@tabler/icons-react'
import styles from './SpotStampOverlay.module.css'

// ─────────────────────────────────────────────
// 型定義
// ─────────────────────────────────────────────
export type SpotStampOverlayStatus = 'acquired' | 'not-acquired' | 'display'

export type SpotStampOverlayProps = {
  /**
   * 表示ステータス
   * 'acquired':     獲得済み（stampImageUrl を表示。タップで再表示ダイアログ起動）
   * 'not-acquired': 未獲得（nonStampImageUrl または灰色プレースホルダー）
   * 'display':      スタンプ操作なし（stampImageUrl をそのまま表示）
   */
  status: SpotStampOverlayStatus
  /** スタンプ画像 URL（acquired・display 時に使用） */
  stampImageUrl?: string
  /** 未獲得スタンプ画像 URL */
  nonStampImageUrl?: string | null
  /**
   * スタンプ形状（管理画面設定値）
   * 0: 円形（デフォルト）
   * 1: 四角
   */
  stampShape?: 0 | 1
  /** 獲得済みスタンプタップ時のコールバック（再表示フロー起動用） */
  onAcquiredClick?: () => void
}

// ─────────────────────────────────────────────
// ユーティリティ
// ─────────────────────────────────────────────
const toStampShape = (stampShape: 0 | 1): StampShape => (stampShape === 1 ? 'square' : 'circular')

// ─────────────────────────────────────────────
// コンポーネント
// ─────────────────────────────────────────────
/**
 * スポット詳細画面の画像右下に表示するスタンプオーバーレイ。
 * stampShape（0=円形 / 1=四角）に応じて形状を切り替える。
 *
 * @example
 * // 獲得済み・円形
 * <SpotStampOverlay status="acquired" stampImageUrl="/stamp.png" stampShape={0} onAcquiredClick={...} />
 * // 未獲得・四角
 * <SpotStampOverlay status="not-acquired" nonStampImageUrl="/none.png" stampShape={1} />
 */
export const SpotStampOverlay = ({
  status,
  stampImageUrl,
  nonStampImageUrl,
  stampShape = 0,
  onAcquiredClick,
}: SpotStampOverlayProps) => {
  const shape = toStampShape(stampShape)

  if (status === 'display') {
    if (!stampImageUrl) return null
    return (
      <div className={styles.container}>
        <Stamp type="Get" src={stampImageUrl} alt="スタンプ" size="Medium" shape={shape} />
      </div>
    )
  }

  if (status === 'acquired') {
    // スタンプ画像が設定されていない → デフォルトのチェックマークを表示
    if (!stampImageUrl) {
      return (
        <button type="button" className={styles.button} onClick={onAcquiredClick} aria-label="スタンプを再表示する">
          <div className={styles.checkmarkContainer}>
            <IconCheck size={48} style={{ color: '#2FA36B' }} aria-label="獲得済み" />
          </div>
        </button>
      )
    }
    return (
      <button type="button" className={styles.button} onClick={onAcquiredClick} aria-label="スタンプを再表示する">
        <Stamp type="Get" src={stampImageUrl} alt="スタンプ" size="Medium" shape={shape} />
      </button>
    )
  }

  // not-acquired: nonStampImageUrl があれば表示、なければプレースホルダー
  return (
    <div className={styles.container}>
      <Stamp
        type={nonStampImageUrl ? 'Get' : 'None'}
        src={nonStampImageUrl ?? undefined}
        alt="スタンプ（未取得）"
        size="Medium"
        shape={shape}
      />
    </div>
  )
}
